import { cls } from '@/shared/lib/classes';

/*
 * A marca do ObraNaLinha, desenhada em código.
 *
 * O símbolo, o nome escrito e as marcas de registro da prancha moram aqui. O
 * kit fica em `docs/obranalinha-identidade/` e continua sendo a fonte para
 * papel, e-mail e redes; o que este arquivo faz é a versão de tela — redesenhada
 * em SVG e texto, e não copiada como imagem, para escalar sem arquivo extra e
 * trocar de cor com o fundo.
 */

interface Tijolo {
  x: number;
  largura: number;
}

interface Fiada {
  y: number;
  tijolos: readonly Tijolo[];
}

const ALTURA_DO_TIJOLO = 3.4;

/**
 * As fiadas, de baixo para cima, alternando a amarração: a ímpar começa com
 * tijolo inteiro, a par com meio tijolo, e é o desencontro entre as duas que faz
 * a parede se ler como parede e não como grade.
 */
const FIADAS: readonly Fiada[] = [
  {
    y: 22.0,
    tijolos: [
      { x: 2, largura: 7.4 },
      { x: 10.2, largura: 7.4 },
      { x: 18.4, largura: 7.6 },
    ],
  },
  {
    y: 17.8,
    tijolos: [
      { x: 2, largura: 3.3 },
      { x: 6.1, largura: 7.4 },
      { x: 14.3, largura: 7.4 },
      { x: 22.5, largura: 3.5 },
    ],
  },
  {
    y: 13.6,
    tijolos: [
      { x: 2, largura: 7.4 },
      { x: 10.2, largura: 7.4 },
      { x: 18.4, largura: 7.6 },
    ],
  },
  // A de cima é a curta: dois tijolos de três.
  {
    y: 9.4,
    tijolos: [
      { x: 2, largura: 7.4 },
      { x: 10.2, largura: 7.4 },
    ],
  },
];

/**
 * Símbolo: a **parede na linha**.
 *
 * Uma parede começada com um lado mais adiantado que o outro, e a linha de
 * pedreiro esticada por cima mostrando onde vai a próxima fiada.
 *
 * ## Por que a última fiada é curta
 *
 * A fiada de cima parar no meio é o assunto do símbolo, não um detalhe de
 * desenho: parede pronta é foto de folheto, parede começada é o que o sistema
 * acompanha. Encher a última fiada mataria a leitura.
 *
 * ## As duas cores
 *
 * Os tijolos herdam `currentColor`, então quem chama escolhe pelo `text-` de
 * sempre. A linha é a segunda cor da marca e vem em `classeDaLinha`, porque ela
 * **inverte com o fundo**: verde nível sobre o claro, cal sobre o escuro — sobre
 * grafite o verde `500` fica quase preto e a linha desaparece justamente onde o
 * nome do sistema promete que ela esteja.
 */
export function Marca({
  className,
  classeDaLinha = 'text-verdete-500',
  titulo = 'ObraNaLinha',
}: {
  className?: string;
  classeDaLinha?: string;
  titulo?: string;
}): React.ReactElement {
  return (
    <svg
      viewBox="0 0 28 28"
      fill="none"
      role="img"
      aria-label={titulo}
      className={cls('h-7 w-7', className)}
    >
      {FIADAS.map((fiada) => (
        <g key={fiada.y}>
          {fiada.tijolos.map((tijolo) => (
            <rect
              key={tijolo.x}
              x={tijolo.x}
              y={fiada.y}
              width={tijolo.largura}
              height={ALTURA_DO_TIJOLO}
              rx="0.6"
              fill="currentColor"
            />
          ))}
        </g>
      ))}

      {/* Linha de pedreiro: o cordão e os dois piquetes que o esticam. */}
      <g className={classeDaLinha}>
        <rect x="1" y="6.1" width="26" height="1.2" rx="0.6" fill="currentColor" />
        <rect x="1" y="3.6" width="1.2" height="6.2" rx="0.6" fill="currentColor" />
        <rect x="25.8" y="3.6" width="1.2" height="6.2" rx="0.6" fill="currentColor" />
      </g>
    </svg>
  );
}

/**
 * O nome escrito, nas duas cores da assinatura: "Obra" no grafite e "NaLinha"
 * no verde nível — a mesma quebra do logotipo horizontal do kit.
 *
 * É texto, e não a imagem do logotipo, porque aqui ele mora ao lado do símbolo
 * numa barra que já tem a fonte de título carregada: como texto ele acompanha o
 * tamanho da linha, quebra se precisar e continua selecionável.
 */
export function NomeDaMarca({
  className,
  tom = 'claro',
}: {
  className?: string;
  /** `claro` é sobre papel; `escuro` é sobre a barra grafite. */
  tom?: 'claro' | 'escuro';
}): React.ReactElement {
  const cores =
    tom === 'escuro'
      ? { obra: 'text-papel-50', linha: 'text-verdete-300' }
      : { obra: 'text-grafite-800', linha: 'text-verdete-500' };

  return (
    <span className={cls('font-display font-extrabold tracking-tight', className)}>
      <span className={cores.obra}>Obra</span>
      <span className={cores.linha}>NaLinha</span>
    </span>
  );
}

/**
 * Marcas de registro do canto — o cruzamento impresso fora da área útil da
 * prancha, usado para alinhar as folhas. Aqui é puramente decorativo e fica
 * fora da árvore de acessibilidade.
 */
export function MarcaDeRegistro({ className }: { className?: string }): React.ReactElement {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      focusable="false"
      className={cls('h-4 w-4', className)}
    >
      <path d="M8 0v16M0 8h16" stroke="currentColor" strokeWidth="1" strokeOpacity="0.45" />
      <circle cx="8" cy="8" r="3.5" stroke="currentColor" strokeWidth="1" strokeOpacity="0.45" />
    </svg>
  );
}
